import type { ViewFieldsFragment } from '@salescore/client-api'
import { getOrganizationIdFromPath, Posthog, POSTHOG_EVENTS } from '@salescore/client-base'
import {
  type CompiledFormViewConfig,
  type CompiledKpiPivotViewConfig,
  type CompiledKpiViewConfig,
  type CompiledSheetViewConfig,
  CORE_CONSTANT,
  type ViewQueryList,
} from '@salescore/core'
import { TimeWindowExecutor } from '@salescore/frontend-common'
import { App } from 'antd'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { useEffect } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'

import { useIsSavingSettings } from '../../hooks/useIsSavingSettings'
import { configAtom } from '../viewConfig'
import { isSavingWithoutInteractionAtom } from './atoms'
import { useSaveCurrentView } from './mutations'

// initializer
export const useWatchView = ({
  onSaveView,
  compiledConfig,
  view: currentView,
  query,
  hasChangeToViewSchema,
  setHasChangeToViewSchema,
}: {
  onSaveView?: (views: ViewFieldsFragment[]) => void
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  view: ViewFieldsFragment
  query: ViewQueryList | undefined
  hasChangeToViewSchema: boolean
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): void => {
  const [isSavingSettings, setIsSavingSettings] = useIsSavingSettings()
  const [isSavingWithoutInteraction, setIsSavingWithoutInteraction] = useRecoilState(isSavingWithoutInteractionAtom)
  const config = useRecoilValue(configAtom)
  const saveCurrentView = useSaveCurrentView(compiledConfig, currentView, query)
  const { message } = App.useApp()
  // プレビュー配下の保存先は KPI 編集フォームの state なので、updateView は成功しても view を返さない。
  // つまりここでは undefined を失敗と解釈できず、戻り値を待たずに変更フラグを落とす必要がある。
  // プレビュー本体・そこからドリルダウンした KPI・その「一覧」パネルは別々の ViewComponent として
  // 描画され、このフックはどれでも動くので、id は 3 つの形を取る。
  const isKpiFormPreviewView =
    currentView.id.startsWith(CORE_CONSTANT.KPI_PREVIEW_VIEW_ID_PREFIX) ||
    currentView.id.startsWith(CORE_CONSTANT.KPI_PREVIEW_DRILL_DOWN_VIEW_ID_PREFIX) ||
    currentView.id.startsWith(
      CORE_CONSTANT.KPI_SHEET_DYNAMIC_VIEW_ID_PREFIX + CORE_CONSTANT.KPI_PREVIEW_DRILL_DOWN_VIEW_ID_PREFIX,
    )

  // 保存中に変えたシートの設定は保存されていないため、フラグを残して未保存と分かるようにする。
  // シート以外はフラグが OFF から ON に変わったときに自動保存するので、残すと再保存が起きずに止まる。
  const clearChangeFlagIfSaved = ({
    view,
    isConfigChangedDuringSave,
  }: Awaited<ReturnType<typeof saveCurrentView>>): void => {
    const hasUnsavedSheetChange = config.type === 'sheet' && isConfigChangedDuringSave
    if ((view !== undefined || isKpiFormPreviewView) && !hasUnsavedSheetChange) {
      setHasChangeToViewSchema(false)
    }
  }

  // ユーザーに対するメッセージやインタラクションなどを表示して保存する
  const saveWithInteraction = async (): Promise<void> => {
    const result = await saveCurrentView(true)
    const { view } = result
    if (view !== undefined) {
      message.success(t(`設定を保存しました`))
      Posthog.track(POSTHOG_EVENTS.save_view_setting, {
        organization_id: getOrganizationIdFromPath(),
        viewId: view.id,
        config: view.config,
      })
    }
    clearChangeFlagIfSaved(result)

    setIsSavingSettings(false)
    if (onSaveView !== undefined) {
      onSaveView([view].filter(isNotNullish))
    }
  }

  // ユーザーに対するメッセージやインタラクションなどを表示しないで保存する
  const saveWithoutInteraction = async (): Promise<void> => {
    const result = await saveCurrentView(false)
    const { view } = result
    setIsSavingWithoutInteraction(false)
    clearChangeFlagIfSaved(result)

    if (onSaveView !== undefined) {
      onSaveView([view].filter(isNotNullish))
    }
  }

  // view(query)を保存する
  // queryをuseEffectで監視したいところだが、viewが切り替わった際に保存が走るなどのミスが不安なので、一旦別にしている。
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: この辺のロジックを整理したい
  const save = async (): Promise<void> => {
    await (isSavingWithoutInteraction ? saveWithoutInteraction() : saveWithInteraction())
  }

  const saveExecutor = new TimeWindowExecutor(save, 1500)

  useEffect(() => {
    const isSaving = isSavingSettings || isSavingWithoutInteraction
    // MEMO: isSavingSettings, isSavingWithoutInteraction, hasChangeToViewSchemaのフラグ更新時にstateをうまくとってこれればuseWatchViewは不要になるはず。
    // 本当はwidthなどのアクションが実行されたトリガーで保存を実行したかったが、
    // 更新後のstateをうまくとってこれなかったので、いったんこのように迂回している
    // シート以外は、 `hasChangeToViewSchema===true` になったらビューの保存を始めている。
    // シートについては、ナビゲーションの「設定を保存」ボタンが押された段階でビューの保存を始めている。
    if ((config.type !== 'sheet' && hasChangeToViewSchema) || (config.type === 'sheet' && isSaving)) {
      saveExecutor.queue([])
    }
  }, [hasChangeToViewSchema, isSavingSettings, isSavingWithoutInteraction])
}
